import { FormEvent, useEffect, useState } from "react";
import axios from "axios";
import { useQueries, useQuery } from "react-query";
import { Link } from "react-router-dom";

import {
  forgetRecentUser,
  getSignedInAdminId,
  readRecentUserIds,
} from "../helpers/recentUsers";
import { buildUserJumpQuery, UserJumpTarget } from "../helpers/userJump";
import { isAeUser } from "../helpers/utils";
import { useRegion } from "../contexts/RegionContext";
import { getUser, getUsers } from "../services/userServices";
import { PAGE_SIZE_DEFAULT } from "../constants";
import { UserType } from "../types/user";
import styles from "../styles/home.module.scss";

const formatPhone = (
  mobile?: string,
  mobileCountryCode?: string
): string | undefined => {
  if (!mobile) return undefined;
  return mobileCountryCode ? `+${mobileCountryCode} ${mobile}` : mobile;
};

const UserActions = ({
  userId,
  appCountry,
}: {
  userId: string;
  appCountry?: string;
}) => (
  <div className={styles.actions}>
    <Link className={styles.action} to={`/users/${userId}`}>
      Open profile
    </Link>
    <Link
      className={styles.action}
      to={`/camp-transactions?userId=${encodeURIComponent(userId)}`}
    >
      CAMP activity
    </Link>
    {isAeUser({ appCountry }) ? (
      <Link
        className={styles.action}
        to={`/ledger-activity?userId=${encodeURIComponent(userId)}`}
      >
        Ledger activity
      </Link>
    ) : null}
  </div>
);

const isInaccessible = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.response?.status === 403 || error.response?.status === 404);

const RecentUsers = () => {
  const { data: adminId, isLoading: isAdminLoading } = useQuery(
    ["signedInAdminId"],
    getSignedInAdminId
  );
  const [recentIds, setRecentIds] = useState<string[]>([]);

  useEffect(() => {
    setRecentIds(adminId ? readRecentUserIds(adminId) : []);
  }, [adminId]);

  const results = useQueries(
    recentIds.map((id) => ({
      queryKey: ["recentUser", id],
      queryFn: () => getUser(id),
      retry: false,
    }))
  );

  const inaccessibleIds = results.flatMap((result, index) =>
    result.isError && isInaccessible(result.error) ? [recentIds[index]] : []
  );
  const inaccessibleKey = inaccessibleIds.join(",");

  useEffect(() => {
    if (!adminId || !inaccessibleKey) return;
    inaccessibleKey.split(",").forEach((id) => forgetRecentUser(adminId, id));
    setRecentIds(readRecentUserIds(adminId));
  }, [adminId, inaccessibleKey]);

  const users = results.flatMap((result) =>
    result.isSuccess && result.data?.id ? [result.data] : []
  );
  const isLoading =
    isAdminLoading || results.some((result) => result.isLoading);
  const hasLoadError = results.some(
    (result) => result.isError && !isInaccessible(result.error)
  );

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Recent users</h2>
      {isLoading ? (
        <p className={styles.sectionHint}>Loading recent users...</p>
      ) : hasLoadError && users.length === 0 ? (
        <p className={styles.sectionHint}>Could not load recent users.</p>
      ) : users.length === 0 ? (
        <p className={styles.sectionHint}>
          Profiles you open in this browser will show up here.
        </p>
      ) : (
        <div className={styles.recentList}>
          {users.map((user) => (
            <article key={user.id} className={styles.card}>
              <Link className={styles.cardTitle} to={`/users/${user.id}`}>
                {user.full_name}
              </Link>
              <p className={styles.cardMeta}>
                {[
                  user.email,
                  formatPhone(user.mobile, user.mobile_country_code),
                ]
                  .filter(Boolean)
                  .join(" · ") || user.id}
              </p>
              <UserActions userId={user.id!} appCountry={user.app_country} />
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const JumpResult = ({ user }: { user: UserType }) => {
  if (!user.id) return null;
  const phone = formatPhone(user.mobile, user.mobile_country_code);

  return (
    <article className={styles.card} data-testid="jump-result">
      <Link className={styles.cardTitle} to={`/users/${user.id}`}>
        {user.full_name}
      </Link>
      <p className={styles.cardMeta}>
        {[user.email, phone].filter(Boolean).join(" · ") || user.id}
      </p>
      <UserActions userId={user.id} appCountry={user.app_country} />
    </article>
  );
};

export default function Home() {
  const { selectedRegion } = useRegion();
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState<UserJumpTarget>();

  const { data, error, isFetching, isFetched } = useQuery<UserType[], Error>(
    ["dashboardUserJump", searchQuery, selectedRegion],
    async () => {
      if (!searchQuery) return [];
      if (searchQuery.kind === "profile") {
        try {
          const user = await getUser(searchQuery.userId);
          const country = user.app_country?.trim().toUpperCase();
          if (
            selectedRegion &&
            country &&
            country !== selectedRegion.toUpperCase()
          ) {
            return [];
          }
          return user.id ? [user] : [];
        } catch (lookupError) {
          if (
            axios.isAxiosError(lookupError) &&
            lookupError.response?.status === 404
          ) {
            return [];
          }
          throw lookupError;
        }
      }

      const result = await getUsers({
        page: 1,
        pageSize: PAGE_SIZE_DEFAULT,
        searchQuery: searchQuery.query,
      });
      return result.users ?? [];
    },
    { enabled: Boolean(searchQuery) }
  );

  const onSearch = (event?: FormEvent) => {
    event?.preventDefault();
    setSearchQuery(buildUserJumpQuery(searchText, selectedRegion));
  };

  useEffect(() => {
    setSearchQuery((current) =>
      current ? buildUserJumpQuery(searchText, selectedRegion) : current
    );
    // Re-run the last search when the selected region changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion]);

  const users = data ?? [];

  return (
    <div>
      <h1 className={styles.pageTitle}>Dashboard</h1>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Find a user</h2>
        <p className={styles.sectionHint}>
          Search by user ID, mobile number, or email.
        </p>
        <form className={styles.searchRow} onSubmit={onSearch}>
          <input
            className={styles.searchInput}
            data-testid="dashboard-user-search"
            type="text"
            value={searchText}
            placeholder="User ID, mobile number, or email"
            onChange={(event) => setSearchText(event.target.value)}
          />
          <button
            className={styles.searchButton}
            data-testid="dashboard-user-search-btn"
            type="submit"
          >
            Search
          </button>
        </form>
        {isFetching ? <p className={styles.status}>Searching...</p> : null}
        {!isFetching && error ? (
          <p className={styles.status}>Could not search for that user.</p>
        ) : null}
        {!isFetching && isFetched && !error && users.length === 0 ? (
          <p className={styles.status}>No user found.</p>
        ) : null}
        {!isFetching && users.length > 0 ? (
          <div className={styles.results}>
            {users.map((user) => (
              <JumpResult key={user.id ?? user.email} user={user} />
            ))}
          </div>
        ) : null}
      </section>
      <RecentUsers />
    </div>
  );
}
